import z from "zod";
import { paginationDtoSchema } from "./monthly-earnings";
import { EServiceCategory, ETaskAction, ETaskStatus, ETaskType, SortOrder } from "../constants";
import { partnerResponseDtoSchema } from ".";
import { paginationMetaSchema } from "./base-api-response";

/**
 * Zod schema for search/filter admin tasks request
 */
export const searchAdminTasksRequestSchema = paginationDtoSchema.extend({
  /** Type of tasks to retrieve (required) */
  taskType: z.nativeEnum(ETaskType),

  /** Filter by task status */
  status: z.nativeEnum(ETaskStatus).optional(),

  /** Filter by service category */
  serviceCategory: z.nativeEnum(EServiceCategory).optional(),

  /** Filter by city */
  city: z.string().trim().min(1).optional(),

  /** Filter tasks created after this timestamp */
  fromDate: z.coerce.number().int().positive().optional(),

  /** Filter tasks created before this timestamp */
  toDate: z.coerce.number().int().positive().optional(),

  /** Filter by partner ID */
  partnerId: z.string().trim().min(1).optional(),

  /** Filter by priority level (1-5) */
  priority: z.coerce.number().int().min(1).max(5).optional(),

  /** Sort field */
  sortBy: z
    .enum(["createdAt", "updatedAt", "priority", "scheduledAt"])
    .optional(),

  /** Sort direction */
  sortDirection: z.nativeEnum(SortOrder).optional(),

  /** Partner name search (server applies ilike) */
  search: z.string().trim().min(1).max(200).optional(),
});

export type ISearchAdminTasksRequest = z.infer<
  typeof searchAdminTasksRequestSchema
>;

/**
 * Partner activation task data DTO schema
 */
export const partnerActivationTaskResponseSchema = z.object({
  partner: partnerResponseDtoSchema,
  onboardingCompletedAt: z.coerce.number().int().positive(),
  trainingCompleted: z.boolean(),
  kycVerified: z.boolean(),
  kitIssued: z.boolean(),
  city: z.string(),
});

export type IPartnerActivationTaskResponse = z.infer<
  typeof partnerActivationTaskResponseSchema
>;

/**
 * Zod schema for bulk task action request
 */
export const bulkTaskActionRequestSchema = z.object({
  /** Array of task actions to process */
  items: z
    .array(
      z.object({
        /** Task ID to process */
        taskId: z.string().min(1),

        /** Action to perform */
        action: z.nativeEnum(ETaskAction),

        /** Optional notes for the action */
        notes: z.string().max(500).optional(),
      }),
    )
    .min(1)
    .max(100),
});

export type IBulkTaskActionRequest = z.infer<
  typeof bulkTaskActionRequestSchema
>;

/**
 * Base Zod schema for admin task response DTO (with data as any for type inference)
 * This is used to derive the TypeScript type
 */
export const baseAdminTaskResponseSchema = z.object({
  /** Unique task identifier */
  id: z.string(),

  /** Type of task */
  taskType: z.nativeEnum(ETaskType),

  /** Current status */
  status: z.nativeEnum(ETaskStatus),

  /** Task-specific data payload */
  data: z.unknown(),

  /** Human-readable task title/description */
  title: z.string(),

  /** Additional context or notes */
  notes: z.string().optional(),

  /** Priority level (1-5, 5 being highest) */
  priority: z.number().int().min(1).max(5),

  /** Task creation timestamp (epoch ms) */
  createdAt: z.number().int().positive(),

  /** Last update timestamp (epoch ms) */
  updatedAt: z.number().int().positive(),

  /** Admin who created/triggered this task */
  createdBy: z.string(),

  /** Admin who last updated this task */
  updatedBy: z.string().optional(),

  /** Admin who approved/rejected */
  reviewedBy: z.string().optional(),

  /** Timestamp when reviewed (epoch ms) */
  reviewedAt: z.number().int().positive().optional(),

  /** Rejection reason if applicable */
  rejectionReason: z.string().optional(),
});

/**
 * Generic admin task response DTO type derived from Zod schema
 * @template TData - Type of task-specific data payload
 */
export type AdminTaskResponseDto<TData = unknown> = Omit<
  z.infer<typeof baseAdminTaskResponseSchema>,
  "data"
> & {
  /** Task-specific data payload */
  data: TData;
};

/**
 * Zod schema for admin tasks list response
 */
export const adminTasksListResponseSchema = z.object({
    items: z.array(baseAdminTaskResponseSchema),
    meta: paginationMetaSchema,
});

/**
 * Type for admin tasks list response
 * @template TData - Type of task-specific data payload in items
 */
export type IAdminTasksListResponse<TData = unknown> = {
    items: Array<AdminTaskResponseDto<TData>>;
    meta: z.infer<typeof paginationMetaSchema>;
};
